/**
 * PATH: packages/ui/src/kanban/components/board-view/cardDensity.ts
 * FILE_ID: UI-KANBAN-UTIL-224
 * PURPOSE: The Kanban card-density (card-size) vocabulary — the 3-stop union,
 *   its cycle order, human labels and the `next` stepper — shared by the
 *   BoardFooter's mobile card-size tile and its desktop "⋯ More" menu row.
 * WHY: FEAT-2026-06-24-KANBAN-CARD-SIZE surfaces card sizing in BOTH the mobile
 *   bottom pane (a 44pt tile) and the desktop overflow menu. Both must step the
 *   SAME 3-stop cycle (Compact → Expanded → Large) so they can never drift.
 *   Lifting the vocabulary into one module lets BoardFooter (orchestrator) and
 *   BoardFooterMoreMenu (child) share it without a circular import.
 * FLOW:
 *   ┌──────────────┐   ┌───────────────────┐   ┌────────────────┐
 *   │ CardDensity  │ → │ nextCardDensity(d) │ → │ next tier +    │
 *   │ + cycle/label│   │ cycles the 3 stops │   │ display label  │
 *   └──────────────┘   └───────────────────┘   └────────────────┘
 * DEPENDENCIES: none (pure data + one pure stepper).
 * RELATED:
 *   - ./BoardFooter.tsx: mobile card-size tile (cycleCardSize).
 *   - ./BoardFooterMoreMenu.tsx: desktop overflow card-size row.
 */

// FEAT-2026-06-24-KANBAN-CARD-SIZE — card size is a 3-stop cycle. Compact =
// 1-line triage tile; Expanded = full Trello face; Large = roomier full face
// with a bigger title (operator: "I might want to see larger cards"). One
// helper drives BOTH the desktop ⋯-menu row and the mobile bottom-pane tile
// so they can never drift.
export type CardDensity = 'compact' | 'expanded' | 'large'
export const CARD_DENSITY_CYCLE: readonly CardDensity[] = ['compact', 'expanded', 'large']
export const CARD_DENSITY_LABEL: Record<CardDensity, string> = {
  compact: 'Compact',
  expanded: 'Expanded',
  large: 'Large',
}
export function nextCardDensity(d: CardDensity): CardDensity {
  const i = CARD_DENSITY_CYCLE.indexOf(d)
  return CARD_DENSITY_CYCLE[(i + 1) % CARD_DENSITY_CYCLE.length]
}
